import React, { useCallback, useEffect, useRef, useState } from 'react';
import ClassLessonAlarmPopup, {
  isClassLessonAlarmTimePassed,
  type ClassLessonAlarmPopupInfo,
} from './ClassLessonAlarmPopup';
import {
  openClassLessonAlarmPopupRelay,
  postClassLessonAlarmRelay,
  type ClassLessonAlarmRelayMessage,
} from '../../utils/classLessonAlarmPopupRelay';
import {
  addClassLessonAlarmDismissed,
  claimClassLessonAlarmSound,
  readClassLessonAlarmDismissed,
  readClassLessonAlarmSchedule,
  releaseClassLessonAlarmSound,
  showClassLessonAlarmDesktopNotice,
} from '../../utils/classLessonAlarmCrossTab';
import { loadClassLessonAlarmSettings } from '../../utils/classLessonAlarmSettings';
import {
  classLessonAlarmGaugeEndsAtMs,
  getMinutesBeforeClassLessonAlarm,
  isClassLessonAlarmTriggerLate,
  playClassLessonAlarmSound,
  preloadClassLessonAlarmSounds,
  stopAllClassLessonAlarmSounds,
} from '../../utils/classLessonAlarmSound';
import { toISODate } from '../../utils/date';

/**
 * 프레젠테이션 탭(메인·수업)의 수업 알람.
 * - 메인 탭 상태를 중계받아 표시하고,
 * - 메인 탭이 숨겨져 멈춰 있어도 공유된 오늘 울림 대상으로 이 탭이 직접 팝업·소리를 낸다.
 * 알람끄기/닫기는 모든 탭으로 액션을 보내 같은 상태를 유지한다.
 */
const ClassLessonAlarmPopupRelay: React.FC<{ workspaceId: string }> = ({ workspaceId }) => {
  const [open, setOpen] = useState(false);
  const [info, setInfo] = useState<ClassLessonAlarmPopupInfo | null>(null);
  const channelRef = useRef<BroadcastChannel | null>(null);
  const infoRef = useRef(info);
  infoRef.current = info;
  const minimizedRef = useRef(false);
  const triggeredRef = useRef<Set<string>>(new Set());

  const stopLocal = useCallback(() => {
    stopAllClassLessonAlarmSounds();
    minimizedRef.current = false;
    setOpen(false);
    setInfo(null);
  }, []);

  const closeLocal = useCallback(() => {
    minimizedRef.current = true;
    setOpen(false);
  }, []);

  useEffect(() => {
    if (!workspaceId.trim()) return undefined;
    const channel = openClassLessonAlarmPopupRelay(workspaceId);
    channelRef.current = channel;
    if (!channel) return undefined;
    const onMessage = (e: MessageEvent<ClassLessonAlarmRelayMessage>) => {
      const msg = e.data;
      if (!msg) return;
      if (msg.kind === 'state') {
        /* 메인 탭에서 알람끄기 — 이 탭이 소리 담당이었을 수 있음 */
        if (!msg.open && !msg.info) stopAllClassLessonAlarmSounds();
        setInfo(msg.info);
        setOpen(msg.open);
        minimizedRef.current = !msg.open && !!msg.info;
      } else if (msg.kind === 'action') {
        if (msg.action === 'stop') stopLocal();
        else if (msg.action === 'close') closeLocal();
      }
    };
    channel.addEventListener('message', onMessage);
    postClassLessonAlarmRelay(channel, { kind: 'request-sync' });
    postClassLessonAlarmRelay(channel, { kind: 'presence', alive: true });
    const heartbeat = window.setInterval(() => {
      postClassLessonAlarmRelay(channel, { kind: 'presence', alive: true });
    }, 2000);
    const onUnload = () => postClassLessonAlarmRelay(channel, { kind: 'presence', alive: false });
    window.addEventListener('pagehide', onUnload);
    return () => {
      window.clearInterval(heartbeat);
      window.removeEventListener('pagehide', onUnload);
      postClassLessonAlarmRelay(channel, { kind: 'presence', alive: false });
      channel.removeEventListener('message', onMessage);
      channel.close();
      channelRef.current = null;
    };
  }, [closeLocal, stopLocal, workspaceId]);

  /** 로컬 울림 엔진 — 메인 탭과 같은 triggerKey 규칙이라 소리는 탭 간 1회만 재생 */
  useEffect(() => {
    const sid = workspaceId.trim();
    if (!sid) return undefined;
    void preloadClassLessonAlarmSounds();
    const check = () => {
      if (!loadClassLessonAlarmSettings(sid).masterEnabled) return;
      const now = new Date();
      const todayIso = toISODate(now);
      Array.from(triggeredRef.current).forEach((k) => {
        if (!k.startsWith(todayIso)) triggeredRef.current.delete(k);
      });

      const active = infoRef.current;
      if (active && minimizedRef.current && isClassLessonAlarmTimePassed(active.time, now)) {
        minimizedRef.current = false;
        setInfo(null);
      }

      const dismissed = readClassLessonAlarmDismissed(sid, todayIso);
      for (const target of readClassLessonAlarmSchedule(sid, todayIso)) {
        const minutesBefore = getMinutesBeforeClassLessonAlarm(target.time, target.offset, now);
        if (minutesBefore == null) continue;
        const dismissKey = `${target.time}-${target.offset}`;
        if (dismissed.has(dismissKey)) continue;
        const triggerKey = `${todayIso}-${target.time}-${target.offset}-${minutesBefore}-${target.channel}`;
        if (triggeredRef.current.has(triggerKey)) continue;
        triggeredRef.current.add(triggerKey);

        const name = `${target.gradeLabel} ${target.periodLabel}`;
        const channelLabel = target.channel === 'dedicated' ? '전담수업' : '일반수업';
        const cur = infoRef.current;
        const isSameSession = !!cur && cur.time === target.time && cur.offset === target.offset;
        const lateLastPhase = minutesBefore === 1 && isClassLessonAlarmTriggerLate(now);
        const gaugeEndsAtMs = classLessonAlarmGaugeEndsAtMs(target.time, minutesBefore, now);
        setInfo((prev) =>
          prev && prev.time === target.time && prev.offset === target.offset
            ? { ...prev, channelLabel, name, ...(lateLastPhase ? { startsAtMs: gaugeEndsAtMs } : {}) }
            : {
                time: target.time,
                offset: target.offset,
                phaseMinutes: minutesBefore,
                channelLabel,
                name,
                startsAtMs: gaugeEndsAtMs,
              }
        );
        if (!isSameSession) minimizedRef.current = false;
        if (!minimizedRef.current) setOpen(true);

        // 구간 시작을 놓쳐 늦게 열린 경우 이 구간 소리는 생략(마지막 1분 구간은 늦어도 울림)
        if (isClassLessonAlarmTriggerLate(now) && minutesBefore > 1) break;

        void claimClassLessonAlarmSound(sid, triggerKey).then(async (won) => {
          if (!won) return;
          if (readClassLessonAlarmDismissed(sid, todayIso).has(dismissKey)) return;
          stopAllClassLessonAlarmSounds();
          const audio = playClassLessonAlarmSound(target.offset, target.soundKind, minutesBefore);
          audio.loop = false;
          const played = await audio.play().then(
            () => true,
            () => false
          );
          if (!played) releaseClassLessonAlarmSound(sid, triggerKey);
          if (document.visibilityState !== 'visible') {
            showClassLessonAlarmDesktopNotice({
              title: `수업 알람 · ${minutesBefore}분 전`,
              body: `${name} (${target.time} 시작)`,
              tag: `cp-class-lesson-alarm:${todayIso}-${target.time}-${target.offset}`,
              onClick: () => {
                minimizedRef.current = false;
                setOpen(true);
              },
            });
          }
        });
        break;
      }
    };
    check();
    const id = window.setInterval(check, 1000);
    return () => {
      window.clearInterval(id);
      stopAllClassLessonAlarmSounds();
    };
  }, [workspaceId]);

  const handleStopAlarm = () => {
    const cur = infoRef.current;
    if (cur) {
      addClassLessonAlarmDismissed(workspaceId, toISODate(new Date()), `${cur.time}-${cur.offset}`);
    }
    stopLocal();
    postClassLessonAlarmRelay(channelRef.current, { kind: 'action', action: 'stop' });
  };
  const handleClosePopup = () => {
    closeLocal();
    postClassLessonAlarmRelay(channelRef.current, { kind: 'action', action: 'close' });
  };

  return (
    <ClassLessonAlarmPopup
      open={open}
      info={info}
      onStopAlarm={handleStopAlarm}
      onClosePopup={handleClosePopup}
    />
  );
};

export default ClassLessonAlarmPopupRelay;
